import { formatShortUsDate } from "@/lib/format";
import type { SpecType } from "@/api/contracts/specifications";

// The details page's display rules — the Spec header, the Version dropdown, the
// banners, the breadcrumb, the heading, the metadata line, the page title — as
// pure functions over the spec summary (#694).
//
// **The shapes are declared here rather than imported from the wire face**, for
// the reason `@/lib/catalog-item-detail` states: the client graph may never
// import `@/server/*`, type-only imports included. Each is the narrow slice of
// the summary a rule reads, so the summary the server function answers with
// satisfies it structurally.
//
// Every string and condition below is the donor's; the parity enumeration on
// #694 names the source line of each.

/** The fields a version's labels read. */
export interface SpecVersionLabelFacts {
  isPublished: boolean;
  versionCode: string | null;
}

/**
 * One Version dropdown entry — `VersionLabel` (`VersionSelect.tsx:83-95`): the
 * **spec's** name, never the version's, then ` (v<code>)` when the version is
 * published and carries a code. A draft, or a published version whose code is
 * blank, reads the bare name — parity (#680 Q3a), so two versions sharing a
 * code are two identical rows.
 */
export function versionOptionLabel(specName: string, version: SpecVersionLabelFacts): string {
  return version.isPublished && version.versionCode
    ? `${specName} (v${version.versionCode})`
    : specName;
}

/** The fields the heading reads off the spec. */
export interface SpecHeadingFacts {
  name: string;
  communityId: string | null;
  community: { externalFullId: string } | null;
}

/**
 * The h2 — `SpecDisplayName` (`SpecDisplayName.tsx:23-30`): the name, then
 * ` (v<code>)` for a coded published version, nothing for an uncoded one, and
 * ` (Draft)` for an unpublished one whatever its code; on a community spec,
 * ` - ` and the matched community's full id, else the raw id.
 *
 * The source's stray whitespace — a trailing space, two before the hyphen —
 * collapses in HTML and is not reproduced.
 */
export function displayName(spec: SpecHeadingFacts, version: SpecVersionLabelFacts): string {
  const versionPart = version.isPublished
    ? version.versionCode
      ? ` (v${version.versionCode})`
      : ""
    : " (Draft)";
  const communityPart = spec.communityId
    ? ` - ${spec.community?.externalFullId || spec.communityId}`
    : "";
  return `${spec.name}${versionPart}${communityPart}`;
}

/**
 * The edit page's h2 — the Editor's `SpecDisplayName` (`SpecEditIsland/
 * components/Header/SpecDisplayName.tsx:20-30`): the name and ` (Draft)`
 * whatever the version's code (the edit route only ever shows a draft), then
 * the community part as the details heading reads it.
 */
export function editDisplayName(spec: SpecHeadingFacts): string {
  const communityPart = spec.communityId
    ? ` - ${spec.community?.externalFullId || spec.communityId}`
    : "";
  return `${spec.name} (Draft)${communityPart}`;
}

/** The fields the breadcrumb root and the title read. */
export interface SpecPlacementFacts {
  type: SpecType;
  division: { name: string } | null;
}

const ROOT_SUFFIX: Record<SpecType, string> = {
  base: "Base Specifications",
  community: "Community Specifications",
  core: "Core Specifications",
};

/**
 * The breadcrumb's first crumb — `generateSpecBreadcrumbs`
 * (`utils/common.ts:180`): the tab's name, prefixed with the division's when
 * there is one. A core spec's division name is `''` in the source
 * (`selectDivisionNameBySpecId`), so Core is never prefixed.
 */
export function breadcrumbRoot(spec: SpecPlacementFacts): string {
  const division = spec.type === "core" ? null : spec.division;
  return division ? `${division.name} ${ROOT_SUFFIX[spec.type]}` : ROOT_SUFFIX[spec.type];
}

/**
 * The breadcrumb's second crumb: the spec's name and ` (v<code>)` when the
 * viewed version carries a code — **whatever its publish state**, unlike the
 * heading (`selectVersionCodeById`).
 */
export function breadcrumbLeaf(specName: string, version: { versionCode: string | null }): string {
  return version.versionCode ? `${specName} (v${version.versionCode})` : specName;
}

/**
 * The edit breadcrumb's third crumb (`EditSpecBreadcrumbs.tsx`), after the
 * root and the bare spec name — the name with no version code, unlike the
 * details page's leaf.
 */
export const EDIT_BREADCRUMB_LEAF = "Edit";

/**
 * The details page's tab title with no name to add — `Layout.tsx:19-24` over
 * blank selectors, less the product suffix: while the page loads, and under a
 * failed summary read.
 */
export const SPEC_PAGE_TITLE = "Specification";

/**
 * The tab title — `Layout`'s `useSetPageTitle` (`Layout.tsx:19-24`) without its
 * product suffix, the per-page-title convention: `Specification | <name>`,
 * then ` | <division>` when the spec has one.
 */
export function pageTitle(spec: { name: string; division: { name: string } | null }): string {
  return spec.division
    ? `${SPEC_PAGE_TITLE} | ${spec.name} | ${spec.division.name}`
    : `${SPEC_PAGE_TITLE} | ${spec.name}`;
}

/** The edit page's tab title with no name to add (`spec-edit-page.tsx:44-48`). */
export const EDIT_PAGE_TITLE = "Specification Edit";

/**
 * The edit page's tab title — `spec-edit-page.tsx:44-48` over
 * `useSetPageTitle`, less the product suffix as every Atlas page title is
 * (shell #657 D1): `Specification Edit | <name>`, then ` | <division>`.
 */
export function editPageTitle(spec: { name: string; division: { name: string } | null }): string {
  return spec.division
    ? `${EDIT_PAGE_TITLE} | ${spec.name} | ${spec.division.name}`
    : `${EDIT_PAGE_TITLE} | ${spec.name}`;
}

// The three banners' copy, verbatim — typos included (`constants.ts:7-16`;
// lifting "use the version dropdown go back" would be a break nobody ruled,
// #680 Q4).
export const LATEST_DRAFT_BANNER =
  "You are viewing latest draft that has unpublished changes. External Users will not see draft changes.";
export const PAST_VERSION_BANNER =
  "You are viewing a past version. Click the link or use the version dropdown go back to latest version.";
export const LATEST_PUBLISHED_BANNER =
  "You are viewing the latest published Version. There is draft in progress.";

export interface VersionBanner {
  severity: "info" | "warning" | "success";
  message: string;
  actionLabel: "View Latest Published" | "View Draft";
  /** The version the action button opens. */
  targetVersionId: number;
}

/** The fields the banner choice reads off the summary. */
export interface SpecBannerFacts {
  lastPublishedVersionId: number | null;
  newestVersionId: number | null;
  versions: readonly { id: number; isPublished: boolean }[];
}

/**
 * Which of the three banners the viewed version shows, or none — the three
 * donor components' conditions, which are mutually exclusive by construction:
 *
 *   - `LatestDraftVersionBanner` — an unpublished version of a spec that has a
 *     last-published version;
 *   - `LastVersionWarning` — a published version that is not the last-published
 *     one (with no pointer, every published version);
 *   - `LatestPublishedVersionBanner` — the last-published version, when the
 *     newest-created version is not published. The source tests
 *     `versions[versionIds[0]].isPublished`, and `newestVersionId` is that
 *     `versionIds[0]`; the owner's restatement over the draft pointer agrees on
 *     every live row, and the source's form is the one pinned (#694 Further
 *     Notes). Its button opens that newest version.
 */
export function bannerFor(
  spec: SpecBannerFacts,
  version: { id: number; isPublished: boolean },
): VersionBanner | null {
  const { lastPublishedVersionId, newestVersionId } = spec;
  if (!version.isPublished) {
    return lastPublishedVersionId === null
      ? null
      : {
          severity: "info",
          message: LATEST_DRAFT_BANNER,
          actionLabel: "View Latest Published",
          targetVersionId: lastPublishedVersionId,
        };
  }
  // The pointer is archive-blind (#704, #702 D3), so a spec with a published
  // version always has one; the source's button to `versions/null` on an
  // archived spec is gone with its blank pointer.
  if (lastPublishedVersionId === null) return null;
  if (version.id !== lastPublishedVersionId) {
    return {
      severity: "warning",
      message: PAST_VERSION_BANNER,
      actionLabel: "View Latest Published",
      targetVersionId: lastPublishedVersionId,
    };
  }
  const newest = spec.versions.find((candidate) => candidate.id === newestVersionId);
  if (newest === undefined || newest.isPublished) return null;
  return {
    severity: "success",
    message: LATEST_PUBLISHED_BANNER,
    actionLabel: "View Draft",
    targetVersionId: newest.id,
  };
}

/**
 * The effective date's **visible** text (`EffectiveDateAlert.tsx:30-35`):
 * `Effective <date>`, and ` - <last-published version's date>` when the viewed
 * version is not the last-published one and that version has a date. Its
 * tooltip is "Effective Date" in both branches.
 */
export function effectiveDateText(effectiveAt: string, lastPublishedEffectiveAt: string | null) {
  const range = lastPublishedEffectiveAt ? ` - ${formatShortUsDate(lastPublishedEffectiveAt)}` : "";
  return `Effective ${formatShortUsDate(effectiveAt)}${range}`;
}

// ---------------------------------------------------------------------------
// The metadata line's parts, for both faces (#1024)
// ---------------------------------------------------------------------------

/**
 * What the metadata line draws, part by part, in its order — each `null` part
 * absent. The line draws from these facts and never tests a URL (#1024
 * Assumption 9): the signed-in face hands it every part it has, the public face
 * fewer (`SpecMetadata.tsx:74,123` gate the count and Based on off the public
 * page, and the share read matches no community).
 */
export interface SpecMetadataFacts {
  division: string | null;
  /** Drawn as "Published m/d/yy" — set only for a published version. */
  publishedAt: string | null;
  effective: {
    effectiveAt: string;
    /** The latest published version's date, when this is not that version. */
    rangeEndAt: string | null;
    isLastPublished: boolean;
  } | null;
  /** "N Communities" — the signed-in face's, on a base spec. */
  communityCount: number | null;
  /** The Base or Core chip; none on a community spec. */
  chip: Exclude<SpecType, "community"> | null;
  /** "Community Name:" — the signed-in face's, on a matched community spec. */
  community: { id: number; name: string; externalFullId: string } | null;
  /** "Based on:" — the signed-in face's, on a community spec. */
  basedOn: {
    specificationId: number;
    specificationName: string;
    versionId: number;
    versionCode: string | null;
  } | null;
}

/** The fields of the share summary the public page's rules read (#1019). */
export interface PublicSpecSummaryFacts {
  name: string;
  type: SpecType;
  communityId: string | null;
  divisionName: string | null;
  versionCode: string | null;
  isPublished: boolean;
  publishedAt: string | null;
  effectiveAt: string | null;
  latestPublished: { id: number; effectiveAt: string | null } | null;
}

/**
 * The public metadata line (`SpecMetadata.tsx` with `isPublicURL()` true):
 * the division, "Published m/d/yy", the Effective date — its range ending at
 * the latest published version's date unless this is that version — and the
 * Base or Core chip. No count, no Based on (both gated off the public page),
 * and no Community Name: the share read skips the community match (#1019).
 */
export function publicMetadataFacts(
  summary: PublicSpecSummaryFacts,
  versionId: number,
): SpecMetadataFacts {
  const isLastPublished = summary.latestPublished?.id === versionId;
  return {
    division: summary.divisionName,
    publishedAt: summary.isPublished ? summary.publishedAt : null,
    effective: summary.effectiveAt
      ? {
          effectiveAt: summary.effectiveAt,
          rangeEndAt: isLastPublished ? null : (summary.latestPublished?.effectiveAt ?? null),
          isLastPublished,
        }
      : null,
    communityCount: null,
    chip: summary.type === "community" ? null : summary.type,
    community: null,
    basedOn: null,
  };
}

/** `ERRORS.LOADER` (`SpecDetailsIsland/constants.ts:4`), both faces'. */
export const LOAD_ERROR = "Error loading specification data. Please refresh the page to try again.";

/** `PAST_VERSION_SPEC_PUBLIC` (`SpecDetailsIsland/constants.ts:10-11`), verbatim. */
export const PAST_VERSION_PUBLIC_ALERT =
  "This is a past version. Click the link to view the latest published version.";

/**
 * The public page's one alert — `LastVersionWarning`'s public branch
 * (`LastVersionWarning.tsx:24-38`): a published version that is not the latest
 * published one. Never a draft — the draft and published banners are gated off
 * the public page (`SpecDetailsIsland.tsx:160-166`) — and never with no latest
 * published version to point at.
 */
export function publicPastVersionAlert(
  summary: Pick<PublicSpecSummaryFacts, "isPublished" | "latestPublished">,
  versionId: number,
): VersionBanner | null {
  const latest = summary.latestPublished;
  if (!summary.isPublished || latest === null || latest.id === versionId) return null;
  return {
    severity: "warning",
    message: PAST_VERSION_PUBLIC_ALERT,
    actionLabel: "View Latest Published",
    targetVersionId: latest.id,
  };
}

/** What the public page's header draws: the h2, the metadata line, the alert. */
export interface PublicHeader {
  heading: string;
  metadata: SpecMetadataFacts;
  alert: VersionBanner | null;
}

/**
 * The public page's header for a version — or, while the summary is the
 * version before's, held as another version's is read, the source's header for
 * a version it has not cached yet: load-metadata caches the viewed version
 * alone, so the spec's facts draw and every version-read part reads empty —
 * "(Draft)" (`SpecDisplayName.tsx:22-30`), the division alone
 * (`SpecMetadata.tsx:39-73`), no alert (`LastVersionWarning.tsx:31`).
 */
export function publicHeader(
  summary: PublicSpecSummaryFacts,
  versionId: number,
  { versionUnread }: { versionUnread: boolean },
): PublicHeader {
  // The share read matches no community, so the raw id stands in its place.
  const spec = { ...summary, community: null };
  if (versionUnread) {
    return {
      heading: displayName(spec, { isPublished: false, versionCode: null }),
      metadata: { ...NO_METADATA, division: summary.divisionName },
      alert: null,
    };
  }
  return {
    heading: displayName(spec, summary),
    metadata: publicMetadataFacts(summary, versionId),
    alert: publicPastVersionAlert(summary, versionId),
  };
}

const NO_METADATA: SpecMetadataFacts = {
  division: null,
  publishedAt: null,
  effective: null,
  communityCount: null,
  chip: null,
  community: null,
  basedOn: null,
};

/** The public page's tab title: the signed-in page's rule over the share summary. */
export function publicPageTitle(summary: Pick<PublicSpecSummaryFacts, "name" | "divisionName">) {
  return pageTitle({
    name: summary.name,
    division: summary.divisionName === null ? null : { name: summary.divisionName },
  });
}
